import { useEffect, useRef, useState } from 'react';

import { useStableCallback } from '@hooks/useStableCallback';
import { ComponentEnhancer, TypeOfPropsFor } from '@utils/types';

const ImageWithFallback: ComponentEnhancer<{
  fallbackImage?: string;
  src: string;
  loading?: 'eager' | 'lazy';
  // Detects pre-hydration blob.core.windows.net failures and uses the fallback.
  checkInitialLoad?: boolean;
}> = ({
  Component,
  src,
  fallbackImage = '/static/images/setur-static.webp',
  loading,
  checkInitialLoad = false,
  ...props
}) => {
  const [imrSrc, setImgSrc] = useState(src);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const setImageRef = useStableCallback((element: HTMLImageElement | null) => {
    imageRef.current = element;
  });
  const onError = useStableCallback(() =>
    setImgSrc((currentSrc) =>
      currentSrc === fallbackImage ? currentSrc : fallbackImage
    )
  );

  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  useEffect(() => {
    if (
      !checkInitialLoad ||
      src !== imrSrc ||
      imrSrc === fallbackImage ||
      !imageRef.current
    ) {
      return;
    }

    const image = imageRef.current;
    if (image.complete && image.naturalWidth === 0) {
      setImgSrc(fallbackImage);
    }
  }, [checkInitialLoad, fallbackImage, imrSrc, src]);

  const componentProps = {
    ...(props as TypeOfPropsFor<typeof Component>),
    loading,
    src: imrSrc,
    onError,
    ...(checkInitialLoad ? { ref: setImageRef } : {}),
  } as TypeOfPropsFor<typeof Component>;

  return <Component {...componentProps} />;
};

export default ImageWithFallback;
